import 'reflect-metadata'; //should be first import
import { Component, HostListener, Inject, OnInit, Renderer2 } from '@angular/core';
import { AuthService } from './services/auth/auth.service';
import {
  LocalStorageService,
  StartService,
  TerritoriesService,
  TrackingService,
} from '@cpak/main-features-lib/services';
import {
  CountriesTerritoriesFacade,
  COUNTRY_TERRITORIES_FEATURE_KEY,
  PackInFocusFacade,
  USER_PREFERENCES_KEY,
  UserFacade,
  UserPreferencesFacade,
  UserPreferencesState,
} from '@cpak/main-features-lib/data-store';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { isTouchDevice } from '@cpak/shared/utils';
import { CountryAndTerritoriesState } from '@cpak/interfaces';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { JAPAN_LANGUAGE, MAX_TERRITORIES_LENGTH_WITHOUT_PAGINATION, TRANSLATION_LOCALES_MAP } from '@cpak/consts';
import { TranslocoService } from '@ngneat/transloco';
import { TranslationService } from './localization/translation.service';
import { environment } from '../environments/environment';
import { PlantsEnum } from '@cpak/adp-cpak-quotes-service-models';
import { PackDescriptionType } from '@cpak/enums';

@Component({
  selector: 'cpak-root',
  template: `
    <div [class.japan-typography]="useJapanTypography$ | async">
      <router-outlet></router-outlet>
    </div>
  `,
})
export class AppComponent implements OnInit {
  public useJapanTypography$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private startService: StartService,
    private countriesTerritoriesFacade: CountriesTerritoriesFacade,
    private userFacade: UserFacade,
    @Inject(DOCUMENT) private document: Document,
    private localStorageService: LocalStorageService,
    private territoriesService: TerritoriesService,
    private packInFocusFacade: PackInFocusFacade,
    private userPreferencesFacade: UserPreferencesFacade,
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private trackingService: TrackingService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (!this.authService.isCallBack()) {
      combineLatest([this.startService.getStart(), this.translationService.initTranslation()]).subscribe(
        ([startResponse]) => {
          if (startResponse) {
            const userRoles = startResponse?.data.userInfo.user.roles;
            this.trackingService.initTracking(this.renderer, environment.production, userRoles);
            this.startService.setStartResponse(startResponse);
            this.authService.setAuthState(true);
          }
        }
      );
    }

    this.userPreferencesFacade.getLanguage().subscribe(language => {
      if (language) {
        this.translocoService.setActiveLang(language);
        this.useJapanTypography$.next(language === JAPAN_LANGUAGE);
        const localeId = TRANSLATION_LOCALES_MAP.get(language);
        if (localeId) {
          Promise.all([
            import(`node_modules/@angular/common/locales/${localeId}.mjs`),
            import(`node_modules/@angular/common/locales/extra/${localeId}.mjs`),
          ]).then(([mainModule, extraModule]) => {
            registerLocaleData(mainModule.default, localeId, extraModule.default);
          });
        }
      } else {
        this.userPreferencesFacade.setUserDefaultLanguage(this.translocoService.getAvailableLangs() as string[]);
      }
    });

    this.userPreferencesFacade.getTimeZone().subscribe(timeZone => {
      if (!timeZone) {
        this.userPreferencesFacade.setUserDefaultTimeZone();
      }
    });

    this.startService
      .getStartResponse()
      .pipe(
        filter(Boolean),
        tap(startResponse => {
          const userId = startResponse.data.userInfo.user.id;
          if (userId) {
            this.userFacade.initUser(userId);
          }

          const plant = startResponse.data.config.defaultPlant;

          if (plant) {
            this.userPreferencesFacade.setPlant(plant);
          }
          const storedPackDescriptionType = this.localStorageService.getItem<UserPreferencesState | null>(
            USER_PREFERENCES_KEY
          )?.packDescriptionDisplay;
          if (!storedPackDescriptionType) {
            this.userPreferencesFacade.setPackDescriptionDisplay(
              plant === PlantsEnum.houston ? PackDescriptionType.COMMON : PackDescriptionType.CUSTOMER
            );
          }
        }),
        switchMap(startResponse => {
          const countriesTerritoriesState = this.localStorageService.getItem<CountryAndTerritoriesState | null>(
            COUNTRY_TERRITORIES_FEATURE_KEY
          );

          let territories$: Observable<string[]> = of([]);

          if (countriesTerritoriesState?.territories?.length && countriesTerritoriesState?.country) {
            const startTerritories = startResponse.data.userInfo.territories;

            territories$ =
              startTerritories.length <= MAX_TERRITORIES_LENGTH_WITHOUT_PAGINATION
                ? of(
                    startTerritories
                      .filter(({ countryIds }) => countryIds?.includes(countriesTerritoriesState.country))
                      .map(({ id }) => id)
                  )
                : this.territoriesService
                    .getUserTerritories(startResponse.data.userInfo.user.id, countriesTerritoriesState.country)
                    .pipe(
                      map(response => response?.data.territories || []),
                      map(territories => territories.map(({ id }) => id)),
                      catchError(() => of([]))
                    );
          }

          return combineLatest([of(startResponse), of(countriesTerritoriesState), territories$]);
        })
      )
      .subscribe(([startResponse, state, territoryIds]) => {
        this.countriesTerritoriesFacade.initCountriesTerritories(
          startResponse.data.userInfo.countryIds,
          territoryIds,
          state,
          startResponse.data.config.countries
        );
      });

    this.countriesTerritoriesFacade
      .getSelectedCountries()
      .pipe(withLatestFrom(this.packInFocusFacade.getData()))
      .subscribe(([country, packInFocus]) => {
        if (packInFocus && country && packInFocus.country !== country) {
          this.packInFocusFacade.clear();
        }
      });
  }

  @HostListener('document:click', ['$event.target']) handleClickEvent(target: HTMLElement): void {
    if (!(target === this.document.activeElement) && isTouchDevice()) {
      (this.document.activeElement as HTMLElement).blur();
    }
  }
}
